"use client";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { IconFlask } from "@tabler/icons-react";
import { Label } from "../ui/label";
import { IUserData } from "@/types";
import { Separator } from "../ui/separator";
import { cn } from "@/lib/utils";

export default function NewUIToggle({ account }: { account?: IUserData }) {
    const isV2 = account?.feature_flags["re-render"] || false;
    const toggleV2 = () => {
        if (isV2) {
            fetch("/api/admin/v2/opt-out", { method: "POST" })
                .then(async (e) => await e.json())
                .then((e) => {
                    if (e?.message === "success") {
                        window.location.reload();
                    }
                });
        } else {
            fetch("/api/admin/v2/opt-in", { method: "POST" })
                .then(async (e) => await e.json())
                .then((e) => {
                    if (e?.message === "success") {
                        window.location.reload();
                    }
                });
        }
    };
    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    variant={isV2 ? "outline" : "default"}
                    className={cn(!isV2 && "bg-accent hover:bg-muted")}
                >
                    {isV2 ? (
                        <>
                            <IconFlask size={18} /> v2
                        </>
                    ) : (
                        <>
                            <IconFlask size={18} /> Update
                        </>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className="flex flex-col gap-0 p-0 sm:h-fit sm:max-h-[min(640px,80vh)] sm:max-w-lg [&>button:last-child]:top-3.5">
                <DialogHeader className="contents space-y-0 text-left">
                    <DialogTitle className="border-b px-6 py-4 text-base">
                        Theme Engine v2
                    </DialogTitle>
                    <div className="overflow-y-auto">
                        <DialogDescription asChild>
                            <div className="px-6 py-4">
                                <div className="space-y-4 text-foreground [&_strong]:font-semibold">
                                    <div className="space-y-4">
                                        <div className="space-y-1">
                                            <Label variant={"headingtoggle"}>
                                                <strong>Open Beta</strong>
                                            </Label>
                                            <Label
                                                variant={"toggle"}
                                                className="font-normal leading-relaxed"
                                            >
                                                This is an open beta, available
                                                to anyone who wants to test out
                                                our new theming engine. We will
                                                import your "legacy" theme into
                                                the new engine.
                                            </Label>
                                        </div>
                                        <Separator />
                                        <div className="space-y-1">
                                            <Label variant={"headingtoggle"}>
                                                <strong>Features</strong>
                                            </Label>
                                            <Label
                                                variant={"toggle"}
                                                className="font-normal leading-relaxed"
                                            >
                                                <ul className="list-inside list-disc">
                                                    <li>
                                                        Save and manage multiple
                                                        themes
                                                    </li>
                                                    <li>
                                                        Improved & additional
                                                        customization options
                                                    </li>
                                                    <li>
                                                        Increased control over
                                                        your full profiles
                                                        appearance
                                                    </li>
                                                </ul>
                                            </Label>
                                        </div>
                                        <Separator />
                                        <div className="space-y-1">
                                            <Label variant={"headingtoggle"}>
                                                <strong>
                                                    Important Information
                                                </strong>
                                            </Label>
                                            <Label
                                                variant={"toggle"}
                                                className="font-normal leading-relaxed"
                                            >
                                                <ul className="list-inside list-disc space-y-1">
                                                    <li>
                                                        When you enable the new
                                                        engine, we'll
                                                        automatically migrate
                                                        your existing theme to
                                                        the new format
                                                    </li>
                                                    <ul className="ml-4 list-inside list-disc">
                                                        <li>
                                                            It may not be a 1 to
                                                            1 migration, and we
                                                            recommend reviewing
                                                            your theme after the
                                                            migration is
                                                            complete
                                                        </li>
                                                    </ul>
                                                    <li>
                                                        You can migrate back to
                                                        the previous engine at
                                                        any time
                                                    </li>
                                                    <ul className="ml-4 list-inside list-disc">
                                                        <li>
                                                            Any changes made to
                                                            your theme in the
                                                            new engine will not
                                                            be carried over to
                                                            the old engine
                                                            should you migrate
                                                            back
                                                        </li>
                                                    </ul>
                                                </ul>
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogFooter className="border-t px-6 py-4 sm:items-center">
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <DialogClose asChild>
                        <Button onClick={toggleV2}>
                            {isV2
                                ? "Switch back to old engine"
                                : "Switch to new engine"}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
